/** A card as the canvas receives it: authored props, plus the one sidecar. */
export interface ZipDependentCard {
  /**
   * The re-pricing recipe (`buildDpAllIn`): attached by `dynamic-page-components-adapter`
   * for the Plan carousel, by `dds-hp-tab-carousel-adapter.ts` for HP. Present only when
   * the card CAN re-price.
   */
  dp_all_in?: unknown;
}

/**
 * Whether ANY card carries a re-pricing recipe — the default rule ALL THREE carousels
 * read, so the CMS team holds one sentence rather than three.
 *
 * ⚠ "CAN RE-PRICE" IS THE WHOLE RULE, not a shortcut for production's name list.
 * `buildDpAllIn` already returns null for an RSN-exempt package, an unknown RSN, an
 * unresolved ladder (`plan-card-rsn.ts:149`), and an all-in that does not reconcile
 * with base + rsn (`plan-card-rsn.ts:168`), so
 * the Entertainment / Óptimo Más exemption falls out of the data and a name list on top
 * would be a branch no input can reach.
 */
export function anyCardReprices(cards: unknown): boolean {
  /* ⚠ `Array.isArray`, not `cards || []`. `GrowCardCarouselProps["cards"]` is
     declared `T[] | T`, so a truthy NON-array is a shape the types allow and a direct
     `<GrowCardCarousel {...blok} />` can hand one over. NOT via `sb-fixer`: its
     `KEEP_AS_LIST` holds "cards" and "panels" and `customArraytoObj` exempts "cards",
     so the page path cannot collapse either. The old `(cards || []).some(...)` threw
     on a bare object, and a throw in a Server Component blanks the page rather than
     dropping a control. */
  if (!Array.isArray(cards)) {
    return false;
  }
  return cards.some((card: ZipDependentCard | null | undefined) => card?.dp_all_in !== null && card?.dp_all_in !== undefined);
}

/**
 * Whether a carousel fills its ZIP slot: an authored `true` forces the control on,
 * anything else defers to the carousel's own `auto` rule.
 *
 * ⚠ THE SWITCH ONLY FORCES THE CONTROL ON. It cannot take it away, and that is
 * deliberate: a Storyblok Boolean carries two values but this decision has three —
 * show, hide, and "nobody ever touched it". Storyblok serialises a blok's whole schema,
 * filling an untouched field with its type's empty value, so an untouched switch
 * publishes `false` and is indistinguishable from one an author deliberately turned off.
 * The published HP story shows both halves of that: `show_tabs` and `show_glow` arrive as
 * real booleans, while `show_zip_code` — not yet in the schema — is absent entirely. If
 * `false` meant hide, adding this field to a blok would take the ZIP off every page
 * already published, with no author involved. So `false` and absent mean the same thing.
 *
 * This is also production's own rule. `plan-card-adapter.ts:1252-1255` reads
 * `isSearchZipCode !== undefined ? (isSearchZipCode === true || hasDynamicRsnFee) :
 * hasDynamicRsnFee`, whose `false` branch falls through to `hasDynamicRsnFee` exactly as
 * its `undefined` branch does — so the whole expression reduces to
 * `isSearchZipCode === true || hasDynamicRsnFee`, and the legacy topper has never had an
 * off switch either. The comment above it claims `false` hides the module; the code it
 * sits on does not.
 *
 * ⚠ `auto` IS THE CALLER'S OWN ANSWER, not a fixed rule. All three carousels happen to
 * pass the same one today — `anyCardReprices`, a ZIP is offered where it can change a
 * number — but the parameter exists because that is NOT the only honest answer. The ZIP
 * is a LOCATION control as much as a price one: it also drives the local channel
 * lineup, which is why the legacy channel listing page carries one on a page with no
 * changing prices at all (`ChnlListingPageV2/IconBanner`, the LOCAL category's
 * `zipCodeModuleProps`). A channel-lineup filler would therefore pass `true` and be
 * right to. Keeping the answer at the call site is what lets it join without this
 * helper learning a per-component branch.
 *
 * ⚠ NOT BYTE PARITY WITH PRODUCTION, and the difference is deliberate. Production's
 * `hasDynamicRsnFee` reads the RAW authored copy for a ZIP-dependent token and
 * subtracts a name-exemption list (`isZipcodeDependentCardPrices`, zip-dependency.ts:94).
 * `anyCardReprices` asks whether `buildDpAllIn` actually LEFT a recipe. They part
 * company where a token is authored but no recipe could be built — the pricing kill
 * switch off, a card with no `package_id`, an all-in that does not reconcile: production
 * shows the control, this hides it. Hiding is the honest answer, because in exactly
 * those cases the price cannot move. The adapter's `zip_dependent` is the field that
 * mirrors production's test, and nothing reads it today.
 *
 * ⚠ EXACT MATCH, no trim and no case-fold. `boolSwitch`
 * (`dds-hp-tab-carousel-adapter.ts:116`) and the organism's own `isOff`
 * (`card-contract.server.ts:19`) both read a switch exactly; a third dialect here would
 * make `" TRUE "` draw the ZIP on a blok where it leaves the tabs alone.
 */
export function shouldShowZipCode(showZipCode: boolean | string | undefined, auto: boolean): boolean {
  return showZipCode === true || showZipCode === "true" || auto;
}
